import { defineStore } from 'pinia';
import { 
  getCuentoValoraciones, 
  enviarValoracion, 
  verificarValoracionUsuario, 
  getEstadisticasValoraciones 
} from '@/services/cuentos';
import { useAuthStore } from '@/stores/auth';

// Función para obtener el token de autenticación
const getAuthToken = () => {
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(";").shift();
    }
    return null;
  };

  const tokenCookie = getCookie('orsai_token');
  if (!tokenCookie) {
    return null;
  }

  try {
    const decodedToken = atob(tokenCookie);
    const [userId, userToken] = decodedToken.split(':');
    return userToken;
  } catch (error) {
    console.error('Error decodificando token:', error);
    return null;
  }
};

export const useValoracionesStore = defineStore('valoraciones', {
  state: () => ({
    valoraciones: {},
    estadisticas: {},
    valoracionesUsuario: {},
    loading: false,
    error: null
  }),

  getters: {
    /**
     * Obtiene las valoraciones de un cuento específico
     */
    getValoracionesByDocumentId: (state) => (documentId) => {
      return state.valoraciones[documentId] || [];
    },

    /**
     * Obtiene las estadísticas de un cuento específico
     */
    getEstadisticasByDocumentId: (state) => (documentId) => {
      return state.estadisticas[documentId] || {
        promedio: 0,
        totalValoraciones: 0,
        distribucion: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      };
    },

    /**
     * Obtiene la valoración del usuario actual para un cuento
     */
    getValoracionUsuario: (state) => (documentId) => {
      return state.valoracionesUsuario[documentId] || null;
    },

    /**
     * Verifica si el usuario actual ya valoró un cuento
     */
    usuarioYaValoro: (state) => (documentId) => {
      return state.valoracionesUsuario[documentId] !== null && 
             state.valoracionesUsuario[documentId] !== undefined;
    }
  },

  actions: {
    /**
     * Carga las valoraciones de un cuento
     */
    async cargarValoraciones(documentId) {
      this.loading = true;
      this.error = null;
      
      try {
        const data = await getCuentoValoraciones(documentId);
        this.valoraciones[documentId] = data.valoraciones || [];
        this.estadisticas[documentId] = data.estadisticas || {
          promedio: 0,
          totalValoraciones: 0,
          distribucion: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        };
      } catch (error) {
        this.error = 'Error al cargar las valoraciones';
        console.error('Error cargando valoraciones:', error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Carga las estadísticas de valoraciones de un cuento
     */
    async cargarEstadisticas(documentId) {
      try {
        const data = await getEstadisticasValoraciones(documentId);
        this.estadisticas[documentId] = data;
      } catch (error) {
        console.error('Error cargando estadísticas:', error);
        // Usar valores por defecto si hay error
        this.estadisticas[documentId] = {
          promedio: 0,
          totalValoraciones: 0,
          distribucion: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        };
      }
    },

    /**
     * Verifica si el usuario actual ya valoró un cuento
     */
    async verificarValoracionUsuario(documentId) {
      const authStore = useAuthStore();
      const usuario = authStore.usuarioActual;
      
      if (!usuario || !usuario.email) {
        this.valoracionesUsuario[documentId] = null;
        return null;
      }

      try {
        const valoracion = await verificarValoracionUsuario(documentId, usuario.email);
        this.valoracionesUsuario[documentId] = valoracion.valoracion;
        return valoracion.valoracion;
      } catch (error) {
        console.error('Error verificando valoración del usuario:', error);
        this.valoracionesUsuario[documentId] = null;
        return null;
      }
    },

    /**
     * Envía una valoración para un cuento
     */
    async enviarValoracion(documentId, rating) {
      const authStore = useAuthStore();
      const usuario = authStore.usuarioActual;
      
      if (!usuario) {
        throw new Error('Debes estar autenticado para valorar un cuento');
      }

      // Validar que el usuario tenga los datos requeridos
      if (!usuario.id || !usuario.email) {
        throw new Error('Datos de usuario incompletos');
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await enviarValoracion(documentId, rating, usuario.id, usuario.email);

        // Actualizar la valoración del usuario
        this.valoracionesUsuario[documentId] = {
          rating,
          userId: usuario.id,
          userEmail: usuario.email,
          cuentoDocumentId: documentId,
          createdAt: new Date().toISOString()
        };

        // Recargar estadísticas
        await this.cargarEstadisticas(documentId);

        return response;
      } catch (error) {
        this.error = 'Error al enviar la valoración';
        console.error('Error enviando valoración:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Actualiza una valoración existente
     */
    async actualizarValoracion(documentId, rating) {
      return await this.enviarValoracion(documentId, rating);
    },

    /**
     * Inicializa los datos de valoraciones para un cuento
     */
    async inicializarValoraciones(documentId) {
      await Promise.all([
        this.cargarValoraciones(documentId),
        this.verificarValoracionUsuario(documentId)
      ]);
    },

    /**
     * Limpia los datos de valoraciones
     */
    limpiarValoraciones() {
      this.valoraciones = {};
      this.estadisticas = {};
      this.valoracionesUsuario = {};
      this.error = null;
    }
  }
}); 